import {
  CANVAS_HEIGHT as BANNER_HEIGHT,
  CANVAS_WIDTH as BANNER_WIDTH,
  DESKTOP_SAFE_HEIGHT,
  DESKTOP_SAFE_WIDTH,
  MOBILE_SAFE_HEIGHT,
  MOBILE_SAFE_WIDTH,
  TABLET_SAFE_HEIGHT,
  TABLET_SAFE_WIDTH,
} from '../components/youtube-banner-resizer/bannerGeometry';
import { isPublishable, PseoEntryBase, Rect, SourceRef } from './pseo/types';

export type { Rect } from './pseo/types';

export type SafeZonePlacement = 'reels' | 'story' | 'stories-reels-ads' | 'banner' | 'shorts' | 'video' | 'video-pin';

export interface SafeZoneSpec extends PseoEntryBase {
  platform: string;
  placement: SafeZonePlacement;
  /** Display name, e.g. "Instagram Story". */
  name: string;
  canvas: { width: number; height: number };
  /** Pixels on the canvas kept free of text, logos and CTAs. */
  margins: { top: number; bottom: number; left: number; right: number };
  /** Platform UI that covers the video (labelled rectangles on the canvas). */
  uiElements: Rect[];
  /** Existing hand-written checkers use PNG overlays; new pages draw the overlay from `margins`/`uiElements`. */
  overlaySource: 'png' | 'data';
  /** Existing hand-written page that owns this spec; no dynamic page is generated for it. */
  staticPagePath?: string;
  /** Short plain-text intro for the dynamic page. */
  intro: string;
  /** Discrepancies between official sources, or scope notes (ads vs organic). */
  notes?: string[];
}

const LAST_VERIFIED = '2026-10-16';

const META_SAFE_ZONE_HELP: SourceRef = {
  label: 'Meta Business Help: text overlays and safe zones',
  url: 'https://www.facebook.com/business/help/980593475366490',
};

/** Meta: keep roughly 14% top, 35% bottom and 6% of each side free on 9:16 Stories and Reels. */
const META_VERTICAL_MARGINS = { top: 269, bottom: 672, left: 65, right: 65 };
const META_NOTE =
  'Meta states the safe zone as percentages (about 14% top, 35% bottom, 6% each side); pixel values are those percentages of 1080×1920, rounded.';

const vertical = { width: 1080, height: 1920 };

const metaUiElements = (bottomLabel: string): Rect[] => [
  { x: 0, y: 0, width: 1080, height: META_VERTICAL_MARGINS.top, label: 'Profile icon & account name' },
  {
    x: 0,
    y: 1920 - META_VERTICAL_MARGINS.bottom,
    width: 1080,
    height: META_VERTICAL_MARGINS.bottom,
    label: bottomLabel,
  },
];

const bannerTop = (BANNER_HEIGHT - MOBILE_SAFE_HEIGHT) / 2;

export const SAFE_ZONE_SPECS: SafeZoneSpec[] = [
  // ---- Existing hand-written checkers (PNG overlays). Margins measured from the overlay assets. ----
  {
    slug: 'instagram-safe-zone-checker',
    primaryQuery: 'instagram reels safe zone',
    platform: 'Instagram',
    placement: 'reels',
    name: 'Instagram Reels',
    canvas: vertical,
    margins: { top: 297, bottom: 422, left: 49, right: 49 },
    uiElements: [{ x: 0, y: 1498, width: 1080, height: 422, label: 'Account name, caption & audio' }],
    overlaySource: 'png',
    staticPagePath: '/safe-zone-checker/instagram-safe-zone-checker',
    intro: 'Preview the Instagram Reels interface over your 9:16 video before you post.',
    sourceUrls: [
      META_SAFE_ZONE_HELP,
      {
        label: 'Meta Ads Guide: Instagram Reels',
        url: 'https://www.facebook.com/business/ads-guide/update/video/instagram-reels',
      },
    ],
    notes: [
      'Margins match the organic Reels overlay this checker draws (measured from the overlay asset at 1080×1920).',
      'Meta’s ad guidance is more conservative: about 269 px top, 672 px bottom and 65 px each side.',
    ],
    lastVerified: LAST_VERIFIED,
    status: 'verified',
    changelog: [{ date: LAST_VERIFIED, change: 'Margins measured from overlay and cross-checked with Meta guidance.' }],
    faqs: [],
  },
  {
    slug: 'tiktok-safe-zone-checker',
    primaryQuery: 'tiktok safe zone',
    platform: 'TikTok',
    placement: 'video',
    name: 'TikTok',
    canvas: vertical,
    margins: { top: 232, bottom: 607, left: 148, right: 238 },
    uiElements: [
      { x: 0, y: 1313, width: 1080, height: 607, label: 'Username, caption & sound' },
      { x: 842, y: 232, width: 238, height: 1081, label: 'Profile, like, comment & share' },
    ],
    overlaySource: 'png',
    staticPagePath: '/safe-zone-checker/tiktok-safe-zone-checker',
    intro: 'Preview the TikTok interface over your 9:16 video before you post.',
    sourceUrls: [
      {
        label: 'TikTok Ads: Auction In-Feed Ads',
        url: 'https://ads.tiktok.com/help/article/tiktok-auction-in-feed-ads',
      },
    ],
    notes: [
      'Margins match the overlay this checker draws (measured from the overlay asset at 1080×1920).',
      'TikTok publishes exact safe-zone pixels only inside its downloadable ad templates; its help text says the safe area shrinks as captions get longer.',
    ],
    lastVerified: LAST_VERIFIED,
    status: 'draft',
    changelog: [
      { date: LAST_VERIFIED, change: 'Margins measured from overlay; no official pixel figures in TikTok help text.' },
    ],
    faqs: [],
  },
  {
    slug: 'youtube-safe-zone-checker',
    primaryQuery: 'youtube shorts safe zone',
    platform: 'YouTube',
    placement: 'shorts',
    name: 'YouTube Shorts',
    canvas: vertical,
    margins: { top: 309, bottom: 380, left: 49, right: 49 },
    uiElements: [{ x: 0, y: 1540, width: 1080, height: 380, label: 'Channel name & description' }],
    overlaySource: 'png',
    staticPagePath: '/safe-zone-checker/youtube-safe-zone-checker',
    intro: 'Preview the YouTube Shorts interface over your 9:16 video before you publish.',
    sourceUrls: [
      {
        label: 'Google Ads Help: safe zones for vertical video ads on YouTube',
        url: 'https://support.google.com/google-ads/answer/9128498',
      },
    ],
    notes: [
      'Margins match the organic Shorts overlay this checker draws (measured from the overlay asset at 1080×1920).',
      'Google’s vertical video ads diagram is stricter: 288 px top, 672 px bottom, 48 px left and 192 px right.',
    ],
    lastVerified: LAST_VERIFIED,
    status: 'verified',
    changelog: [
      { date: LAST_VERIFIED, change: 'Margins measured from overlay and cross-checked with Google Ads diagram.' },
    ],
    faqs: [],
  },

  // ---- Data-driven pages (/safe-zone-checker/[slug]) ----
  {
    slug: 'instagram-story-safe-zone-checker',
    primaryQuery: 'instagram story safe zone',
    platform: 'Instagram',
    placement: 'story',
    name: 'Instagram Story',
    canvas: vertical,
    margins: META_VERTICAL_MARGINS,
    uiElements: metaUiElements('Call-to-action & reply bar'),
    overlaySource: 'data',
    intro:
      'Instagram Stories are 9:16 (1080×1920). Meta recommends keeping about 14% of the top, 35% of the bottom and 6% of each side free of text, logos and calls to action so the profile icon and the call-to-action never cover them.',
    sourceUrls: [
      {
        label: 'Meta Business Help: design requirements for Instagram Stories ads',
        url: 'https://www.facebook.com/business/help/2222978001316177',
      },
      {
        label: 'Meta Ads Guide: Instagram Stories',
        url: 'https://www.facebook.com/business/ads-guide/update/video/instagram-story',
      },
      META_SAFE_ZONE_HELP,
    ],
    notes: [
      META_NOTE,
      'Meta publishes this guidance for Stories ads; it does not publish separate numbers for organic Stories.',
    ],
    lastVerified: LAST_VERIFIED,
    status: 'verified',
    changelog: [{ date: LAST_VERIFIED, change: 'First published from Meta’s 14/35/6% Stories guidance.' }],
    faqs: [
      {
        question: 'What is the Instagram Story safe zone?',
        answer:
          'On a 1080×1920 Story, keep about 269 px at the top, 672 px at the bottom and 65 px on each side free of text and logos. That is Meta’s guidance of roughly 14% top, 35% bottom and 6% each side.',
      },
      {
        question: 'What size is an Instagram Story?',
        answer: 'Instagram Stories are vertical 9:16. Meta recommends 1080×1920 px.',
      },
      {
        question: 'What covers an Instagram Story?',
        answer:
          'The profile icon and account name at the top, and the call-to-action or reply bar at the bottom. Stickers you add also take space, so keep key text inside the safe area.',
      },
      {
        question: 'Is the Story safe zone the same as Reels?',
        answer:
          'Meta now gives Stories and Reels the same percentages. The Reels interface also adds buttons on the right, so check Reels in the Instagram Reels checker.',
      },
    ],
  },
  {
    slug: 'youtube-banner-safe-zone-checker',
    primaryQuery: 'youtube banner safe zone',
    platform: 'YouTube',
    placement: 'banner',
    name: 'YouTube Banner',
    canvas: { width: BANNER_WIDTH, height: BANNER_HEIGHT },
    margins: {
      top: bannerTop,
      bottom: bannerTop,
      left: (BANNER_WIDTH - MOBILE_SAFE_WIDTH) / 2,
      right: (BANNER_WIDTH - MOBILE_SAFE_WIDTH) / 2,
    },
    uiElements: [
      {
        x: (BANNER_WIDTH - DESKTOP_SAFE_WIDTH) / 2,
        y: (BANNER_HEIGHT - DESKTOP_SAFE_HEIGHT) / 2,
        width: DESKTOP_SAFE_WIDTH,
        height: DESKTOP_SAFE_HEIGHT,
        label: 'Desktop visible area',
      },
      {
        x: (BANNER_WIDTH - TABLET_SAFE_WIDTH) / 2,
        y: (BANNER_HEIGHT - TABLET_SAFE_HEIGHT) / 2,
        width: TABLET_SAFE_WIDTH,
        height: TABLET_SAFE_HEIGHT,
        label: 'Tablet visible area',
      },
    ],
    overlaySource: 'data',
    intro:
      'YouTube channel banners are 2560×1440 px, but only the centered strip shows on every device. Keep text and logos inside the 1546×423 safe area: TVs show the whole image, desktops a full-width strip, and phones only the center.',
    sourceUrls: [
      {
        label: 'YouTube Help: manage your channel branding',
        url: 'https://support.google.com/youtube/answer/10456525',
      },
    ],
    notes: [
      'YouTube Help states the safe area as 1235×338 px at the 2048×1152 minimum size; scaled to 2560×1440 that is about 1546×423 px.',
      'Desktop and tablet strips are the preview areas Kreatli’s banner resizer uses; YouTube does not publish per-device sizes.',
    ],
    lastVerified: LAST_VERIFIED,
    status: 'verified',
    changelog: [{ date: LAST_VERIFIED, change: 'First published from YouTube Help banner guidance.' }],
    faqs: [
      {
        question: 'What is the YouTube banner safe area?',
        answer:
          'The centered region that shows on every device. YouTube Help gives it as 1235×338 px at the 2048×1152 minimum size, which is about 1546×423 px on the recommended 2560×1440 canvas.',
      },
      {
        question: 'What size should a YouTube banner be?',
        answer: 'YouTube recommends 2560×1440 px (16:9), at least 2048×1152 px, and a file of 6 MB or smaller.',
      },
      {
        question: 'Why is my YouTube banner cropped on mobile?',
        answer:
          'Phones show only the center of the banner. Anything outside the 1546×423 safe area can be cut off, so keep your channel name, logo and text inside it.',
      },
      {
        question: 'How do I resize an image to a YouTube banner?',
        answer:
          'Use the free YouTube banner resizer: upload an image, fit or fill it to 2560×1440 and preview TV, desktop, tablet and mobile before downloading.',
      },
    ],
  },
  {
    slug: 'facebook-reels-safe-zone-checker',
    primaryQuery: 'facebook reels safe zone',
    platform: 'Facebook',
    placement: 'reels',
    name: 'Facebook Reels',
    canvas: vertical,
    margins: META_VERTICAL_MARGINS,
    uiElements: metaUiElements('Caption, call-to-action & actions'),
    overlaySource: 'data',
    intro:
      'Facebook Reels are 9:16. Meta recommends leaving about 14% at the top, 35% at the bottom and 6% on each side free of text and logos so the profile icon and call-to-action do not cover them.',
    sourceUrls: [
      {
        label: 'Meta Ads Guide: Facebook Reels',
        url: 'https://www.facebook.com/business/ads-guide/update/video/facebook-facebook-reels',
      },
      META_SAFE_ZONE_HELP,
    ],
    notes: [META_NOTE, 'Meta publishes this guidance for Reels ads; organic Reels use the same interface.'],
    lastVerified: LAST_VERIFIED,
    status: 'verified',
    changelog: [{ date: LAST_VERIFIED, change: 'First published from Meta’s 14/35/6% Reels guidance.' }],
    faqs: [
      {
        question: 'What is the Facebook Reels safe zone?',
        answer:
          'On a 1080×1920 Reel, keep about 269 px at the top, 672 px at the bottom and 65 px on each side free of text and logos (Meta’s 14% / 35% / 6% guidance).',
      },
      {
        question: 'What size is a Facebook Reel?',
        answer: 'Facebook Reels are vertical 9:16. Export at 1080×1920 px.',
      },
      {
        question: 'Is the Facebook Reels safe zone the same as Instagram Reels?',
        answer:
          'Meta gives the same percentages for Facebook and Instagram Reels ads. Preview both, because the button layout differs between the apps.',
      },
    ],
  },
  {
    slug: 'meta-ads-safe-zone-checker',
    primaryQuery: 'meta ads safe zone',
    platform: 'Meta',
    placement: 'stories-reels-ads',
    name: 'Meta Stories & Reels Ads',
    canvas: vertical,
    margins: META_VERTICAL_MARGINS,
    uiElements: metaUiElements('Ad copy & call-to-action button'),
    overlaySource: 'data',
    intro:
      'For Stories and Reels placements across Facebook and Instagram, Meta recommends keeping about 14% of the top, 35% of the bottom and 6% of each side free of text and logos. If your ad carries a disclaimer, leave the bottom 40% free.',
    sourceUrls: [
      META_SAFE_ZONE_HELP,
      {
        label: 'Meta Ads Guide: Facebook Stories',
        url: 'https://www.facebook.com/business/ads-guide/update/video/facebook-story',
      },
      {
        label: 'Meta Ads Guide: Instagram Reels',
        url: 'https://www.facebook.com/business/ads-guide/update/video/instagram-reels',
      },
    ],
    notes: [
      META_NOTE,
      'With disclaimers, Meta asks for the bottom 40% (768 px on 1920) to stay free.',
      'Meta’s Ads Guide recommends uploading 1440×2560; the safe zone percentages are the same.',
    ],
    lastVerified: LAST_VERIFIED,
    status: 'verified',
    changelog: [{ date: LAST_VERIFIED, change: 'First published from Meta Business Help safe zone guidance.' }],
    faqs: [
      {
        question: 'What is the Meta ads safe zone for Stories and Reels?',
        answer:
          'Keep about 14% of the top, 35% of the bottom and 6% of each side free of text and logos. On a 1080×1920 creative that is about 269 px, 672 px and 65 px.',
      },
      {
        question: 'Does the safe zone change if my ad has a disclaimer?',
        answer:
          'Yes. Meta asks you to leave the bottom 40% of the ad free when it includes disclaimers, which is 768 px on a 1920 px tall creative.',
      },
      {
        question: 'Does Ads Manager check safe zones?',
        answer:
          'Ads Manager shows a safe zone guardrail when you preview Stories and Reels placements. Checking the creative before upload avoids re-exports.',
      },
    ],
  },
  {
    slug: 'pinterest-video-pin-safe-zone-checker',
    primaryQuery: 'pinterest safe zone',
    platform: 'Pinterest',
    placement: 'video-pin',
    name: 'Pinterest Video Pin',
    canvas: vertical,
    margins: { top: 270, bottom: 790, left: 65, right: 195 },
    uiElements: [
      { x: 0, y: 0, width: 1080, height: 270, label: 'Top interface' },
      { x: 0, y: 1130, width: 1080, height: 790, label: 'Pin details & buttons' },
      { x: 885, y: 270, width: 195, height: 860, label: 'Right-side actions' },
    ],
    overlaySource: 'data',
    intro:
      'Pinterest full-bleed video Pins are 9:16 (1080×1920). Pinterest asks you to keep text and logos out of the top 270 px, bottom 790 px, left 65 px and right 195 px, which leaves an 820×860 px safe area.',
    sourceUrls: [
      { label: 'Pinterest Help: review Pin specs', url: 'https://help.pinterest.com/en/article/review-pin-specs' },
      {
        label: 'Pinterest Business Help: product specs',
        url: 'https://help.pinterest.com/en/business/article/pinterest-product-specs',
      },
    ],
    notes: [
      'Pinterest’s Idea ads spec gives a smaller bottom margin (440 px); this checker uses the stricter video Pin figure.',
    ],
    lastVerified: LAST_VERIFIED,
    status: 'verified',
    changelog: [{ date: LAST_VERIFIED, change: 'First published from Pinterest Help Pin specs.' }],
    faqs: [
      {
        question: 'What is the Pinterest video Pin safe zone?',
        answer:
          'On a 1080×1920 video Pin, keep text and logos out of the top 270 px, bottom 790 px, left 65 px and right 195 px. The safe area left in the middle is 820×860 px.',
      },
      {
        question: 'What size should a Pinterest video Pin be?',
        answer: 'For full-bleed vertical video, Pinterest specifies 9:16 or 1080×1920 px.',
      },
      {
        question: 'Why is the bottom margin so large on Pinterest?',
        answer:
          'Pin details, the title and buttons sit at the bottom of a full-screen Pin. Pinterest’s Idea ads use a 440 px bottom margin, but video Pins call for 790 px.',
      },
    ],
  },
  {
    slug: 'snapchat-safe-zone-checker',
    primaryQuery: 'snapchat safe zone',
    platform: 'Snapchat',
    placement: 'video',
    name: 'Snapchat Snap Ads',
    canvas: vertical,
    margins: { top: 150, bottom: 330, left: 0, right: 0 },
    uiElements: [],
    overlaySource: 'data',
    intro: 'Snap Ads are vertical 9:16 (1080×1920).',
    sourceUrls: [
      { label: 'Snapchat Business Help: safe zones', url: 'https://businesshelp.snapchat.com/s/article/safe-zones' },
      {
        label: 'Snapchat Business Help: app install specs',
        url: 'https://businesshelp.snapchat.com/s/article/specs-app-install',
      },
    ],
    notes: [
      'Snapchat publishes pixel buffers only for App Install attachments (top 150 px, bottom 330 px); standard Snap Ads point to the Ads Manager toggle. Kept as draft until an official figure exists.',
    ],
    lastVerified: LAST_VERIFIED,
    status: 'draft',
    changelog: [{ date: LAST_VERIFIED, change: 'Draft: no official pixel buffers for standard Snap Ads.' }],
    faqs: [],
  },
  {
    slug: 'linkedin-video-safe-zone-checker',
    primaryQuery: 'linkedin video safe zone',
    platform: 'LinkedIn',
    placement: 'video',
    name: 'LinkedIn Video',
    canvas: vertical,
    margins: { top: 0, bottom: 0, left: 0, right: 0 },
    uiElements: [],
    overlaySource: 'data',
    intro: 'LinkedIn vertical video ads are 9:16.',
    sourceUrls: [
      { label: 'LinkedIn Help: video ads specifications', url: 'https://www.linkedin.com/help/lms/answer/a424737' },
    ],
    notes: ['LinkedIn does not publish a safe zone for video. Kept as draft.'],
    lastVerified: LAST_VERIFIED,
    status: 'draft',
    changelog: [{ date: LAST_VERIFIED, change: 'Draft: LinkedIn publishes no safe zone.' }],
    faqs: [],
  },
];

export const getSafeZoneSpec = (slug: string) => SAFE_ZONE_SPECS.find((spec) => spec.slug === slug);

/** Specs that get a generated `/safe-zone-checker/[slug]` page. */
export const getDynamicSafeZoneSpecs = () =>
  SAFE_ZONE_SPECS.filter((spec) => isPublishable(spec) && spec.overlaySource === 'data' && !spec.staticPagePath);

export const safeZoneCheckerPath = (spec: SafeZoneSpec) => spec.staticPagePath ?? `/safe-zone-checker/${spec.slug}`;

/** Every published safe zone checker (hand-written + generated), for hubs, sibling links and the sitemap. */
export const getPublishedSafeZoneCheckers = () =>
  SAFE_ZONE_SPECS.filter((spec) => spec.staticPagePath || isPublishable(spec));
